import { useInfoSocietesOriasQuery } from "@src/data/infoSocieteOrias/hook/useInfoSocietesOriasQuery";
import {
  collectSirens,
  filterOriasRadiees,
  STAGES_SCAN_ORIAS,
} from "@src/helper/cq-orias-radiees.helper";
import { isRecette } from "@src/helper/environnement.helper";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { ControleQuinquennalApi } from "../controle-quinquennal.api";
import { controleQuinquennalKeys } from "./controle-quinquennal.keys";

/**
 * Des pages courtes, enchaînées séquentiellement : le balayage porte sur toute
 * la population et ne doit ni retarder l'affichage de la liste principale ni
 * saturer la gateway — le bandeau se complète au fil des pages reçues.
 */
const TAILLE_PAGE_SCAN = 10;

/**
 * La liste des contrôles dont l'entreprise est radiée se reconstitue en croisant
 * les contrôles avec les statuts ORIAS, servis par le cache de la gateway. Le
 * balayage porte sur toute la population, d'où la restriction à la recette où la
 * fonctionnalité est exposée.
 */
export const useCQOriasRadieesQuery = () => {
  const controles = useInfiniteQuery({
    queryKey: controleQuinquennalKeys.oriasRadiees(),
    // Le SIREN vient des métriques, pas de la liste : sans lui rien ne peut
    // être croisé avec l'ORIAS. La page vaut un paquet de métriques.
    queryFn: async ({ pageParam }) => {
      const page = await ControleQuinquennalApi.list({
        page: pageParam,
        limit: TAILLE_PAGE_SCAN,
        pipelineStages: STAGES_SCAN_ORIAS,
      });
      const metriques = await ControleQuinquennalApi.metriques(page.data);
      return {
        ...page,
        data: page.data.map((ligne) => ({
          ...ligne,
          ...metriques[ligne.id],
        })),
      };
    },
    initialPageParam: 1,
    getNextPageParam: (dernierePage, pages) =>
      pages.length * TAILLE_PAGE_SCAN < dernierePage.total
        ? pages.length + 1
        : undefined,
    staleTime: Infinity,
    enabled: isRecette,
  });

  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = controles;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) {
      // cancelRefetch: false — un déclenchement concurrent (re-render pendant
      // qu'une page part) rejoint la requête en cours au lieu de la relancer.
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const lignes = useMemo(
    () => (controles.data?.pages ?? []).flatMap(({ data }) => data),
    [controles.data]
  );
  const sirens = useMemo(() => collectSirens(lignes), [lignes]);
  const orias = useInfoSocietesOriasQuery(sirens);

  const rows = useMemo(
    () => filterOriasRadiees(lignes, orias.data?.bySiren ?? {}),
    [lignes, orias.data]
  );

  return {
    rows,
    sirens,
    lastUpdate: orias.data?.lastUpdate ?? undefined,
    // Le balayage automatique échoue côté serveur : son verdict est la seule
    // trace qui permette d'afficher la barre d'erreur au vérificateur.
    dernierAppelEnEchec: orias.data?.dernierAppelEnEchec ?? false,
    isLoading: controles.isLoading || orias.isLoading,
    isError: controles.isError || orias.isError,
  };
};
